import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
    HiOutlineHome,
    HiOutlineBriefcase,
    HiOutlineChatAlt2,
    HiOutlineCog,
    HiOutlineBell,
    HiOutlineSearch,
    HiOutlineEmojiHappy
} from "react-icons/hi";

// Static Mock Data for Creator's POV (talking to brands)
const mockCreatorChatsData = [
    { 
        id: "c1", 
        name: "Adidas", 
        campaignName: "Product Reel",
        snippet: "Great, we love the draft!", 
        time: "10:30 AM", 
        unread: 2, 
        avatarColor: "bg-black",
        logo: "fa-brands fa-adidas text-white text-xl",
        isTyping: false,
        messages: [
            { id: 1, text: "Here is the first draft of the reel, let me know if you need any edits.", time: "Yesterday, 4:00PM", isCreator: true },
            { id: 2, text: "Great, we love the draft! Can you make the logo at the end slightly larger?", time: "10:30AM", isCreator: false },
        ],
        statuses: [
            { id: 1, label: "Campaign Accepted", status: "completed", highlighted: true },
            { id: 2, label: "Content Created", status: "completed" },
            { id: 3, label: "Brand Review", status: "current" },
            { id: 4, label: "Revisions", status: "pending" },
            { id: 5, label: "Content Approved", status: "pending", highlighted: false },
            { id: 6, label: "Campaign Completed", status: "pending", highlighted: false },
            { id: 7, label: "Payment Received", status: "pending", highlighted: false }
        ]
    },
    { 
        id: "c2", 
        name: "L'Oréal", 
        campaignName: "Summer Glow Selection",
        snippet: "Thanks for applying!", 
        time: "Yesterday", 
        read: true, 
        avatarColor: "bg-gray-900",
        logo: "fa-solid fa-droplet text-white", // Placeholder icon 
        isTyping: false,
        messages: [
            { id: 1, text: "Hi! I just submitted my application for the Summer Glow campaign.", time: "Monday, 9:00AM", isCreator: true },
            { id: 2, text: "Thanks for applying! We are reviewing profiles and will get back to you shortly.", time: "Yesterday, 2:15PM", isCreator: false },
        ],
        statuses: [
            { id: 1, label: "Applied", status: "completed", highlighted: true },
            { id: 2, label: "Application Under Review", status: "current", highlighted: false },
            { id: 3, label: "Campaign Accepted", status: "pending" },
            { id: 4, label: "Content Created", status: "pending" },
            { id: 5, label: "Brand Review", status: "pending", highlighted: false },
            { id: 6, label: "Campaign Completed", status: "pending", highlighted: false },
            { id: 7, label: "Payment Received", status: "pending", highlighted: false }
        ]
    },
    { 
        id: "c3", 
        name: "Red Bull", 
        campaignName: "Extreme Sports Promo",
        snippet: "Payment has been processed.", 
        time: "Oct 12", 
        read: true, 
        avatarColor: "bg-red-600",
        logo: "fa-solid fa-bolt text-white",
        isTyping: false,
        messages: [
            { id: 1, text: "The video is live!", time: "Oct 10, 11:00AM", isCreator: true },
            { id: 2, text: "Amazing engagement so far. Payment has been processed.", time: "Oct 12, 10:00AM", isCreator: false },
        ],
        statuses: [
            { id: 1, label: "Campaign Accepted", status: "completed", highlighted: true },
            { id: 2, label: "Content Created", status: "completed" },
            { id: 3, label: "Brand Review", status: "completed" },
            { id: 4, label: "Content Approved", status: "completed" },
            { id: 5, label: "Campaign Completed", status: "completed", highlighted: true },
            { id: 6, label: "Payment Processing", status: "completed", highlighted: false },
            { id: 7, label: "Payment Received", status: "current", highlighted: true }
        ]
    }
];

const CreatorMessages = () => {
    const [messageInput, setMessageInput] = useState("");
    const [activeChatId, setActiveChatId] = useState(mockCreatorChatsData[0].id);

    const activeChat = mockCreatorChatsData.find(c => c.id === activeChatId) || mockCreatorChatsData[0];

    return (
        <div className="flex h-screen bg-[#FDFBF7]">
            {/* Creator Sidebar reused from perfectly working Creator Dashboard flow */}
            <Sidebar />

            <main className="flex-1 overflow-hidden flex flex-col h-screen p-4">
                {/* Top Header */}
                <header className="flex justify-between items-center mb-6 shrink-0 h-12">
                    <div className="flex-1 max-w-2xl bg-white border border-gray-100 rounded-xl px-4 py-3 shadow-sm mx-4 invisible">
                        {/* Hidden search bar just to keep layout spacing accurate */}
                    </div>
                    
                    <div className="flex items-center gap-6 ml-4">
                        <button className="relative text-gray-500 hover:text-gray-700">
                            <HiOutlineBell className="text-2xl" />
                        </button>
                        <div className="flex items-center gap-3">
                            <span className="text-sm font-bold text-gray-800 hidden md:block">Johan Doe</span>
                            <div className="w-10 h-10 rounded-full bg-[#0044CC] flex items-center justify-center text-white font-bold shadow-md overflow-hidden">
                                J
                            </div>
                        </div>
                    </div>
                </header>

                {/* Inner Messaging Container */}
                <div className="flex-1 overflow-hidden flex flex-col min-h-0 bg-white rounded-3xl shadow-sm border border-gray-100 mx-auto w-full max-w-7xl">
                    <div className="flex flex-1 overflow-hidden min-h-0">
                        {/* LEFT COLUMN: Chat List */}
                        <div className="w-80 border-r border-gray-100 flex flex-col bg-white shrink-0">
                            <div className="p-6 pb-4 border-b border-gray-50 shrink-0">
                                <h2 className="text-xl font-bold text-gray-900 mb-6">Messages</h2>
                                <div className="relative">
                                    <input 
                                        type="text" 
                                        placeholder="Search brands" 
                                        className="w-full bg-gray-50 text-gray-700 text-sm rounded-xl py-3 pl-4 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-100"
                                    />
                                    <HiOutlineSearch className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
                                </div>
                            </div>
                            
                            <div className="flex-1 overflow-y-auto custom-scrollbar">
                                {mockCreatorChatsData.map((chat) => (
                                    <div 
                                        key={chat.id} 
                                        onClick={() => setActiveChatId(chat.id)}
                                        className={`flex items-start gap-3 p-4 cursor-pointer transition-colors border-b border-gray-50
                                            ${activeChatId === chat.id ? 'bg-blue-50/50' : 'hover:bg-gray-50'}
                                        `}
                                    >
                                        <div className={`w-10 h-10 rounded-full ${chat.avatarColor} shrink-0 flex items-center justify-center shadow-sm`}>
                                            <i className={chat.logo}></i>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex justify-between items-center mb-1">
                                                <h4 className="font-bold text-gray-900 text-sm">{chat.name}</h4>
                                                <span className="text-xs text-gray-400">{chat.time}</span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <p className="text-sm text-blue-500 font-medium truncate pr-2">{chat.snippet}</p>
                                                {chat.unread && (
                                                    <span className="w-5 h-5 bg-[#0044CC] text-white text-[10px] font-bold rounded-full flex items-center justify-center shrink-0">
                                                        {chat.unread}
                                                    </span>
                                                )}
                                                {chat.read && (
                                                    <div className="flex text-blue-500 text-sm shrink-0">
                                                        <i className="fa-solid fa-check-double text-[10px]"></i>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* CENTER COLUMN: Chat Thread */}
                        <div className="flex-1 flex flex-col bg-[#FDFDFD] min-w-0">
                            {/* Thread Header */}
                            <div className="px-6 py-4 border-b border-gray-100 bg-white flex items-center gap-4 shrink-0 h-20">
                                <div className={`w-10 h-10 ${activeChat.avatarColor} rounded-full flex items-center justify-center shrink-0 shadow-sm`}>
                                    <i className={activeChat.logo}></i>
                                </div>
                                <div>
                                    <h3 className="font-bold text-gray-900 text-sm">{activeChat.name}</h3>
                                    <p className="text-blue-500 text-xs font-medium">{activeChat.campaignName}</p>
                                </div>
                            </div>

                            {/* Messages Scroll Area */}
                            <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar bg-gray-50/50">
                                {activeChat.messages.map((msg) => (
                                    <div key={msg.id}>
                                        <div className={`flex w-full ${msg.isCreator ? 'justify-end' : 'justify-start'}`}>
                                            <div className={`flex items-end gap-2 max-w-[70%] ${msg.isCreator ? 'flex-row-reverse' : 'flex-row'}`}>
                                                
                                                {/* Avatar */}
                                                <div className={`w-6 h-6 rounded-full shrink-0 flex items-center justify-center overflow-hidden text-xs font-bold text-white shadow-sm
                                                    ${msg.isCreator ? 'bg-[#0044CC]' : activeChat.avatarColor}
                                                `}>
                                                    {msg.isCreator ? 'J' : <i className={`${activeChat.logo} text-[10px] transform scale-75`}></i>}
                                                </div>

                                                {/* Bubble */}
                                                <div className="flex flex-col">
                                                    <div className={`px-5 py-3.5 rounded-2xl text-sm leading-relaxed shadow-sm
                                                        ${msg.isCreator 
                                                            ? 'bg-[#0044CC] text-white rounded-tr-sm' 
                                                            : 'bg-white border border-gray-100 text-gray-800 rounded-tl-sm'
                                                        }
                                                    `}>
                                                        {msg.text}
                                                    </div>
                                                    {msg.time && (
                                                        <span className={`text-[10px] text-gray-400 mt-1 ${msg.isCreator ? 'text-right' : 'text-left'}`}>
                                                            {msg.time}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Input Area */}
                            <div className="p-6 bg-white shrink-0 border-t border-gray-50">
                                <div className="flex items-center gap-4">
                                    <button className="text-gray-400 hover:text-gray-600 transition-colors">
                                        <HiOutlineEmojiHappy className="text-2xl" />
                                    </button>
                                    <input 
                                        type="text" 
                                        placeholder="Write a message to the brand..."
                                        value={messageInput}
                                        onChange={(e) => setMessageInput(e.target.value)}
                                        className="flex-1 bg-transparent text-sm focus:outline-none placeholder-gray-400"
                                    />
                                    <button className="w-10 h-10 bg-[#0044CC] text-white rounded-xl flex items-center justify-center hover:bg-blue-700 transition-colors shadow-md">
                                        <i className="fa-solid fa-paper-plane text-sm"></i>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* RIGHT COLUMN: Campaign Status Tracker */}
                        <div className="w-80 border-l border-gray-100 bg-white opacity-[0.98] shrink-0 overflow-y-auto custom-scrollbar p-8">
                            <h2 className="text-xl font-bold text-gray-900 mb-8">Status Tracker</h2>
                            
                            <div className="relative border-l border-dashed border-gray-200 ml-4 space-y-8 pb-4">
                                {activeChat.statuses.map((status) => {
                                    const isCompleted = status.status === "completed";
                                    const isCurrent = status.status === "current";
                                    
                                    return (
                                        <div key={status.id} className="relative flex items-center pl-8">
                                            {/* Status Dot/Icon */}
                                            <div className={`absolute -left-3 w-6 h-6 rounded-full flex items-center justify-center bg-white border-2
                                                ${isCompleted ? 'border-[#0044CC] text-[#0044CC]' : ''}
                                                ${isCurrent ? 'border-[#0044CC]' : ''}
                                                ${!isCompleted && !isCurrent ? 'border-gray-300' : ''}
                                            `}>
                                                {isCompleted ? (
                                                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                ) : isCurrent ? (
                                                    <div className="w-2.5 h-2.5 bg-[#0044CC] rounded-full"></div>
                                                ) : null}
                                            </div>

                                            {/* Status Label Pill */}
                                            <div className={`w-full px-4 py-2.5 rounded-xl flex justify-between items-center transition-all
                                                ${status.highlighted ? 'bg-[#F4F1FB]' : 'bg-transparent border border-gray-200'}
                                                ${isCurrent ? 'border-[#0044CC]' : ''}
                                            `}>
                                                <span className="text-xs font-bold text-gray-900">{status.label}</span>
                                                {isCompleted && (
                                                    <div className="w-4 h-4 rounded-full border border-green-500 flex items-center justify-center text-green-500">
                                                        <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    </div>
                                                )}
                                                {isCurrent && (
                                                    <div className="w-4 h-4 rounded-full border border-green-500 flex items-center justify-center text-green-500">
                                                        <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

// Extracted Sidebar to reuse identically
const Sidebar = () => {
    const location = useLocation();
    const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

    return (
        <aside className="w-64 bg-white border-r border-gray-100 flex flex-col hidden md:flex shrink-0 z-20">
            <div className="p-6 flex items-center gap-2 text-black font-bold text-xl">
                <img src="/logo.png" alt="Braccet" className="w-8 h-8 object-contain" />
                <span>Braccet</span>
            </div>

            <nav className="flex-1 px-4 py-6 space-y-2">
                <SidebarItem icon={<HiOutlineHome />} label="Dashboard" to="/creator/dashboard" />
                <SidebarItem icon={<HiOutlineBriefcase />} label="Campaigns" to="/creator/find-work" />
                <SidebarItem icon={<span className="font-bold text-lg">(B)</span>} label="Brands" to="/creator/brands" />
                <SidebarItem icon={<HiOutlineChatAlt2 />} label="Messages" to="/creator/messages" active={isActive('/creator/messages')} />
                <SidebarItem icon={<HiOutlineCog />} label="Settings" to="/creator/profile" />
            </nav>

            <div className="p-4 border-t border-gray-100">
                <button className="flex w-full items-center gap-3 p-2 rounded-xl text-gray-500 font-bold hover:bg-gray-50 hover:text-red-500 transition-colors">
                    <svg className="w-6 h-6 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                    Logout
                </button>
            </div>
        </aside>
    );
};

const SidebarItem = ({ icon, label, to, active }) => (
    <Link to={to} className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${active ? 'bg-blue-50 text-[#0044CC]' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`}>
        <span className="text-xl">{icon}</span>
        {label}
    </Link>
);

export default CreatorMessages;
